/**
 * WhatIfSimulator.jsx — iter770 (Feb 2026)
 *
 * "What-If" educational предиктивен симулатор. Пациентът избира
 * до N хипотетични диагнози (чекбоксове по категории), извиква
 * POST /api/breast-intelligence/what-if и вижда в реално време:
 *   • Как биха се променили BHI композитният скор + Twin композит
 *   • Кой близнак пада най-силно и защо
 *   • Кои BHI метаболитни компоненти биват засегнати
 *
 * НЕ записва нищо в базата — това е чисто образователен инструмент.
 * Ако потребителят реши да запази някое от състоянията, има бутон
 * „Запази тези диагнози структурирано" → POST /health-conditions/bulk.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Sparkles, Loader2, TrendingUp, TrendingDown,
  Save, RefreshCcw, Info,
} from "lucide-react";
import { useAuth } from "@/App";
import { emitHealthEvent } from "@/utils/healthEvents";

// Curated shortlist of the most-impactful conditions to simulate.
// Each item: {id, name_bg, category, note}. Kept small (~15) so the UI
// stays scannable — the full 38-item canonical list is still available
// via HealthConditionsCard for anyone who needs an unusual diagnosis.
const SIM_CONDITIONS = [
  // ── Metabolic ──
  { id: "insulin_resistance",  name_bg: "Инсулинова резистентност", category: "Метаболитни",  note: "Insulin ↓12, Metab.-инсулинов близнак ↓10" },
  { id: "type2_diabetes",      name_bg: "Диабет тип 2",              category: "Метаболитни",  note: "Insulin ↓12, IGF-1 ↓5, Adiposity ↓8" },
  { id: "metabolic_syndrome",  name_bg: "Метаболитен синдром",       category: "Метаболитни",  note: "Insulin + IGF-1 + Adiposity" },
  { id: "nafld",               name_bg: "NAFLD (мастен ч. дроб)",    category: "Метаболитни",  note: "Inflammation ↓8, Liver-Kidney близнак ↓12" },
  // ── Endocrine ──
  { id: "pcos",                name_bg: "PCOS",                      category: "Ендокринни",   note: "Insulin ↓8, Estrogen ↓10, Хормонален ↓12" },
  { id: "endometriosis",       name_bg: "Ендометриоза",              category: "Ендокринни",   note: "Estrogen ↓, Inflammation, Хормонален ↓10" },
  { id: "hashimoto",           name_bg: "Хашимото",                  category: "Ендокринни",   note: "Щитовиден близнак ↓12, Inflammation ↓" },
  { id: "hypothyroidism",      name_bg: "Хипотиреоидизъм",           category: "Ендокринни",   note: "Щитовиден близнак ↓12" },
  // ── Cardio ──
  { id: "hypertension",        name_bg: "Хипертония",                category: "Сърдечно-съдови", note: "Cardio близнак ↓10" },
  { id: "dyslipidemia",        name_bg: "Дислипидемия",              category: "Сърдечно-съдови", note: "Cardio ↓10" },
  // ── Autoimmune / inflammation ──
  { id: "rheumatoid_arthritis",name_bg: "Ревматоиден артрит",        category: "Автоимунни",   note: "Inflammation ↓8-18, Възпалителен близнак ↓12" },
  { id: "ibd",                 name_bg: "IBD (Crohn/UC)",            category: "Автоимунни",   note: "Inflammation ↓12, Nutrition risk" },
  { id: "psoriasis",           name_bg: "Псориазис",                 category: "Автоимунни",   note: "Inflammation ↓" },
  // ── Genetic / breast ──
  { id: "brca1_positive",      name_bg: "BRCA1 позитивност",         category: "Генетика",     note: "Estrogen ↓ (маркер)" },
  { id: "brca2_positive",      name_bg: "BRCA2 позитивност",         category: "Генетика",     note: "Estrogen ↓ (маркер)" },
  // ── Male-only ──
  { id: "gynecomastia",        name_bg: "Гинекомастия",              category: "Мъжко здраве", note: "Estrogen ↓12, Мъжки здр. близнак ↓12" },
];

const CATEGORIES = ["Метаболитни", "Ендокринни", "Сърдечно-съдови", "Автоимунни", "Генетика", "Мъжко здраве"];

const TWIN_LABEL_BG = {
  bhi:               "BHI (метаболит. среда)",
  inflammation:      "възпалителен близнак",
  hormonal:          "хормонален близнак",
  metabolic_insulin: "метаболитно-инсулинов близнак",
  thyroid:           "щитовиден близнак",
  cardio:            "сърдечно-съдов близнак",
  liver_kidney:      "чернодробно-бъбречен близнак",
  nutritional:       "нутриционен близнак",
  male_health:       "мъжки здравен близнак",
};

function DeltaBadge({ before, after, larger = false }) {
  if (before == null || after == null) return null;
  const d = after - before;
  const cls = d === 0
    ? "bg-slate-100 text-slate-600 ring-slate-300"
    : d > 0
      ? "bg-emerald-100 text-emerald-800 ring-emerald-300"
      : "bg-rose-100 text-rose-800 ring-rose-300";
  const arrow = d === 0 ? "±0" : d > 0 ? `+${d}` : `−${Math.abs(d)}`;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full ring-1 font-bold ${cls} ${larger ? "text-sm" : "text-[11px]"}`}
      data-testid="whatif-delta-badge"
    >
      <span className="tabular-nums">{before} → {after}</span>
      <span className="tabular-nums">({arrow})</span>
    </span>
  );
}

export default function WhatIfSimulator({ isDark = false, bg = true }) {
  const { API, user } = useAuth();
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [result,  setResult]  = useState(null);
  const [error,   setError]   = useState(null);
  const [saving,  setSaving]  = useState(false);

  const toggle = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };

  const runSim = useCallback(async () => {
    if (!user || selected.size === 0) { setResult(null); return; }
    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.post(
        `${API}/breast-intelligence/what-if`,
        { hypothetical_conditions: Array.from(selected) },
        { timeout: 20000 },
      );
      setResult(data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Грешка при симулация");
      setResult(null);
    } finally {
      setLoading(false);
    }
  }, [API, user, selected]);

  // Auto-run 500 ms after each selection change.
  useEffect(() => {
    if (selected.size === 0) { setResult(null); return; }
    const t = setTimeout(runSim, 500);
    return () => clearTimeout(t);
  }, [selected, runSim]);

  const clearAll = () => { setSelected(new Set()); setResult(null); };

  const persist = async () => {
    if (!user || selected.size === 0) return;
    setSaving(true);
    try {
      const items = Array.from(selected).map((cid) => ({ condition_id: cid, severity: "self_reported" }));
      const { data } = await axios.post(`${API}/health-conditions/bulk`, { items });
      emitHealthEvent("conditions-changed", { added: items.map(i => i.condition_id) });
      toast.success(`Запазени ${data?.count ?? items.length} диагнози. BHI и Twin-ите се обновяват.`);
      // After save, the simulation is now "reality" — reset UI.
      setResult(null);
      setSelected(new Set());
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Грешка при запис на диагнозите.");
    } finally { setSaving(false); }
  };

  const byCategory = useMemo(() => {
    const map = {};
    for (const cat of CATEGORIES) map[cat] = [];
    for (const c of SIM_CONDITIONS) {
      if (!map[c.category]) map[c.category] = [];
      map[c.category].push(c);
    }
    return map;
  }, []);

  const outerCls = isDark
    ? "bg-slate-900/60 border border-violet-500/40 ring-1 ring-violet-500/20"
    : "bg-gradient-to-br from-violet-50/60 to-white border border-violet-200";

  if (!user) return null;

  return (
    <section
      data-testid="whatif-simulator"
      className={`p-5 rounded-2xl ${outerCls} mb-5`}
    >
      <header className="flex items-start justify-between gap-3 mb-3">
        <div>
          <div className={`text-[10px] uppercase font-bold tracking-widest mb-0.5 ${isDark ? "text-violet-300" : "text-violet-700"}`}>
            Симулатор
          </div>
          <h3 className={`text-base font-bold ${isDark ? "text-slate-100" : "text-slate-900"}`}>
            <Sparkles className="inline w-4 h-4 mr-1 -mt-0.5" />
            {bg ? "Симулатор „Какво ако имам…\" — влияние на диагнози" : "What-If Diagnosis Simulator"}
          </h3>
          <p className={`text-[12px] mt-1 ${isDark ? "text-slate-300" : "text-slate-600"}`}>
            {bg
              ? "Изберете хипотетични диагнози и вижте в реално време как биха се променили BHI и 20-те дигитални близнака."
              : "Pick hypothetical conditions and see how BHI + your 20 twins would shift."}
          </p>
        </div>
        {selected.size > 0 && (
          <button
            onClick={clearAll}
            data-testid="whatif-clear-btn"
            className={`text-[11px] inline-flex items-center gap-1 px-2 py-1 rounded-lg ${isDark ? "bg-slate-800 text-slate-300 hover:bg-slate-700" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`}
          >
            <RefreshCcw className="w-3 h-3" /> {bg ? "Изчисти" : "Clear"}
          </button>
        )}
      </header>

      {/* Category groups */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-4">
        {CATEGORIES.map((cat) => (
          <div key={cat} data-testid={`whatif-cat-${cat}`}>
            <div className={`text-[10px] font-bold uppercase mb-1 ${isDark ? "text-slate-400" : "text-slate-500"}`}>{cat}</div>
            <div className="flex flex-wrap gap-1.5">
              {byCategory[cat].map((c) => {
                const on = selected.has(c.id);
                return (
                  <button
                    key={c.id}
                    onClick={() => toggle(c.id)}
                    title={c.note}
                    data-testid={`whatif-toggle-${c.id}`}
                    className={`text-[11px] px-2 py-1 rounded-full font-semibold ring-1 transition ${
                      on
                        ? "bg-violet-600 text-white ring-violet-600 shadow-sm"
                        : isDark
                          ? "bg-slate-800 text-slate-200 ring-slate-700 hover:bg-slate-700"
                          : "bg-white text-slate-700 ring-slate-300 hover:bg-violet-50"
                    }`}
                  >
                    {c.name_bg}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {/* Result panel */}
      {loading && (
        <div className={`flex items-center gap-2 text-[12px] ${isDark ? "text-slate-300" : "text-slate-600"}`}>
          <Loader2 className="w-4 h-4 animate-spin" />
          {bg ? "Симулираме..." : "Simulating..."}
        </div>
      )}

      {error && (
        <div className="text-[12px] text-rose-600" data-testid="whatif-error">{error}</div>
      )}

      {result && result.unchanged && (
        <div className={`text-[12px] p-3 rounded-lg ${isDark ? "bg-slate-800 text-slate-300" : "bg-slate-100 text-slate-700"}`}>
          <Info className="inline w-3.5 h-3.5 mr-1 -mt-0.5" />
          {result.message}
        </div>
      )}

      {result && !result.unchanged && (
        <div className="space-y-3" data-testid="whatif-result">
          {/* Composite deltas */}
          <div className={`p-3 rounded-xl ${isDark ? "bg-slate-800/70" : "bg-white ring-1 ring-slate-200"}`}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <div className={`text-[10px] uppercase font-bold ${isDark ? "text-violet-300" : "text-violet-700"}`}>BHI композит</div>
                <div className="mt-1">
                  <DeltaBadge
                    before={result.baseline?.bhi_composite}
                    after={result.hypothetical?.bhi_composite}
                    larger
                  />
                </div>
              </div>
              <div>
                <div className={`text-[10px] uppercase font-bold ${isDark ? "text-emerald-300" : "text-emerald-700"}`}>Twin композит</div>
                <div className="mt-1">
                  <DeltaBadge
                    before={result.baseline?.twin_composite}
                    after={result.hypothetical?.twin_composite}
                    larger
                  />
                </div>
              </div>
            </div>

            {/* Biggest impact narrative */}
            {result.deltas?.biggest_change && (
              <div
                data-testid="whatif-biggest-change"
                className={`mt-3 text-[12px] flex items-start gap-2 ${isDark ? "text-slate-300" : "text-slate-700"}`}
              >
                {result.deltas.biggest_change.delta < 0
                  ? <TrendingDown className="w-4 h-4 text-rose-500 mt-0.5" />
                  : <TrendingUp   className="w-4 h-4 text-emerald-500 mt-0.5" />}
                <span>
                  <b>{bg ? "Най-засегнат:" : "Biggest hit:"}</b>{" "}
                  {TWIN_LABEL_BG[result.deltas.biggest_change.twin] || result.deltas.biggest_change.twin}
                  {" — "}
                  <span className="tabular-nums">{result.deltas.biggest_change.before} → {result.deltas.biggest_change.after}</span>
                  {" "}
                  <span className={`font-bold ${result.deltas.biggest_change.delta < 0 ? "text-rose-600" : "text-emerald-600"}`}>
                    ({result.deltas.biggest_change.delta > 0 ? "+" : "−"}{Math.abs(result.deltas.biggest_change.delta)})
                  </span>
                </span>
              </div>
            )}
          </div>

          {/* Per-twin table */}
          {result.deltas?.by_twin?.length > 0 && (
            <div>
              <div className={`text-[10px] uppercase font-bold mb-1 ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                {bg ? "Промени по близнак:" : "Per-twin changes:"}
              </div>
              <div className="flex flex-wrap gap-1.5" data-testid="whatif-by-twin-row">
                {result.deltas.by_twin.map((c) => (
                  <span
                    key={c.twin}
                    data-testid={`whatif-twin-${c.twin}`}
                    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-lg text-[11px] ring-1 ${
                      c.delta < 0
                        ? "bg-rose-50 text-rose-800 ring-rose-200"
                        : "bg-emerald-50 text-emerald-800 ring-emerald-200"
                    }`}
                  >
                    <span className="font-semibold">{TWIN_LABEL_BG[c.twin] || c.twin}</span>
                    <span className="tabular-nums">{c.before}→{c.after}</span>
                    <span className="font-bold">({c.delta > 0 ? `+${c.delta}` : `−${Math.abs(c.delta)}`})</span>
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Why — condition modifiers on metabolic components */}
          {Object.keys(result.hypothetical?.condition_modifiers || {}).length > 0 && (
            <div>
              <div className={`text-[10px] uppercase font-bold mb-1 ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                {bg ? "Защо BHI пада:" : "Why BHI shifts:"}
              </div>
              <ul className={`text-[11px] space-y-0.5 ml-4 list-disc ${isDark ? "text-slate-300" : "text-slate-700"}`}>
                {Object.entries(result.hypothetical.condition_modifiers).map(([comp, mods]) => (
                  <li key={comp}>
                    <b>{comp}:</b> {mods.join("; ")}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Persist CTA */}
          <div className="flex items-center justify-between gap-3 pt-2">
            <div className={`text-[10px] ${isDark ? "text-slate-500" : "text-slate-500"}`}>
              {bg
                ? "⚠ Симулацията НЕ записва нищо. Резултатите са образователни."
                : "⚠ Simulation does not write anything. Educational only."}
            </div>
            <button
              onClick={persist}
              disabled={saving}
              data-testid="whatif-persist-btn"
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] font-bold ${
                saving
                  ? "bg-slate-300 text-slate-600 cursor-not-allowed"
                  : "bg-violet-600 text-white hover:bg-violet-700"
              }`}
            >
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
              {bg ? "Ако е реално — запази" : "If real — save"}
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
